import { useMutation, useQueryClient, type UseMutationOptions } from "@tanstack/react-query";
import { useFeatureFlag } from "./useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { enqueue, isIndexedDBAvailable, type SyncQueueEntry } from "@/lib/offlineStore";
import { connectivityService } from "@/lib/connectivityService";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const ONLINE_TIMEOUT_MS = 5_000;

/**
 * Detect network-level errors where the request never reached the server.
 * These indicate the device is effectively offline despite navigator.onLine === true.
 */
function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError && /failed to fetch|network/i.test(err.message)) {
    return true;
  }
  if (err instanceof DOMException && err.name === "AbortError") {
    return true;
  }
  if (err && typeof err === "object" && "message" in err) {
    const msg = (err as { message: string }).message;
    if (
      /failed to fetch|networkerror|network request failed|load failed|timeout|abort/i.test(msg)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Race a promise against a timeout. Rejects with a descriptive error on timeout.
 */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Mutation timeout after ${ms / 1000}s`)), ms);
    promise.then(
      (val) => {
        clearTimeout(timer);
        resolve(val);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

interface OfflineMutationOptions<TData, TError, TVariables> {
  /** Standard mutation function (used when online) */
  mutationFn: (variables: TVariables) => Promise<TData>;
  /** Entity type for the sync queue */
  entityType: SyncQueueEntry["entityType"];
  /** Operation type */
  operation: SyncQueueEntry["operation"];
  /** Convert variables to a sync queue payload */
  toPayload: (variables: TVariables) => Record<string, unknown>;
  /** Image refs extractor (optional) */
  getImageRefs?: (variables: TVariables) => string[];
  /** Async preparation before offline queueing (e.g. storing images in IndexedDB) */
  prepareOffline?: (variables: TVariables) => Promise<void>;
  /** Callback after successful offline queueing (for optimistic cache updates) */
  onOfflineQueued?: (payload: Record<string, unknown>) => void;
  /** Query keys to invalidate on success */
  invalidateKeys?: string[][];
  /** Additional TanStack Query mutation options */
  mutationOptions?: Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn">;
}

/**
 * Wrapper around TanStack Query useMutation that supports offline queueing.
 *
 * - Online: Normal mutation behavior (direct to Supabase) with timeout
 * - Online + network error / timeout + feature flag: Falls back to offline queue
 * - Offline + feature flag enabled: Writes to sync-queue, shows toast
 * - Offline + feature flag disabled: Normal behavior (will fail with network error)
 *
 * Uses a connectivity pre-check (HEAD request, 2s) before attempting the online path
 * to avoid hanging on auth.getUser() when the network is actually unreachable.
 */
export function useOfflineMutation<TData = unknown, TError = Error, TVariables = void>({
  mutationFn,
  entityType,
  operation,
  toPayload,
  getImageRefs,
  prepareOffline,
  onOfflineQueued,
  invalidateKeys,
  mutationOptions,
}: OfflineMutationOptions<TData, TError, TVariables>) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enabled: offlineEnabled } = useFeatureFlag(
    "offline-mode",
    FEATURE_FLAGS["offline-mode"] ?? false,
  );

  return useMutation<TData, TError, TVariables>({
    ...mutationOptions,
    mutationFn: async (variables: TVariables) => {
      // Read connectivity status synchronously from the central service
      const reachable = connectivityService.isOnline;

      // If the service says we're online, try the real mutation
      if (reachable) {
        try {
          const result = await withTimeout(mutationFn(variables), ONLINE_TIMEOUT_MS);
          return result;
        } catch (err) {
          // On network error or timeout, fall through to offline queueing if supported
          if (offlineEnabled && isIndexedDBAvailable() && isNetworkError(err)) {
            console.warn(
              "[OfflineMutation] Network error/timeout while online, falling back to offline queue:",
              err,
            );
          } else {
            throw err;
          }
        }
      }

      // Offline path: queue for later sync
      if (offlineEnabled && isIndexedDBAvailable()) {
        // Await async preparation (e.g. storing images in IndexedDB)
        await prepareOffline?.(variables);

        const payload = toPayload(variables);
        const imageRefs = getImageRefs?.(variables);

        await enqueue({
          entityType,
          operation,
          payload,
          timestamp: new Date().toISOString(),
          retryCount: 0,
          imageRefs,
        });

        toast.info(t("common.savedOffline"), {
          description: t("common.willSyncWhenOnline"),
        });

        // Optimistic cache update callback
        onOfflineQueued?.(payload);

        // Return a synthetic result to satisfy the type
        return payload as unknown as TData;
      }

      // Letzter Fallback: Wenn offline und kein Offline-Support,
      // nicht die mutationFn aufrufen (die garantiert hängt bei auth.getUser),
      // sondern einen klaren Fehler werfen
      if (!reachable) {
        throw new Error(t("common.noNetworkEnableOffline"));
      }
      return mutationFn(variables);
    },
    onSuccess: (data, variables, context) => {
      // Only invalidate queries when online (use live check)
      if (connectivityService.isOnline) {
        if (invalidateKeys) {
          for (const key of invalidateKeys) {
            queryClient.invalidateQueries({ queryKey: key });
          }
        }
        (
          mutationOptions?.onSuccess as (
            data: unknown,
            variables: unknown,
            context: unknown,
          ) => void
        )?.(data, variables, context);
      }
    },
  });
}
